
import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase'; // ✅ KORRIGIERT: Richtiger Import
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import Select from '../../components/base/Select';
import Modal from '../../components/base/Modal';

interface Kontakt {
  id: string;
  name: string;
  firma: string;
  telefon: string;
  email: string;
  kategorie: string;
  notiz: string;
  strasse: string;
  plz: string;
  ort: string;
  land: string;
  erstelltAm: string;
}

export default function Kontakte() {
  const { user } = useAuth();
  const [contacts, setContacts] = useState<Kontakt[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Kontakt | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    firma: '',
    telefon: '',
    email: '',
    kategorie: '',
    notiz: '',
    strasse: '',
    plz: '',
    ort: '',
    land: 'Schweiz'
  });

  // Load contacts from Supabase
  useEffect(() => {
    if (user) {
      loadContacts();
    }
  }, [user]);

  const loadContacts = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('daten')
        .select('*')
        .eq('user_id', user.id)
        .eq('data_type', 'contact')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const contactData = data.map(item => ({
        id: item.id,
        ...item.data,
        erstelltAm: item.created_at
      }));

      setContacts(contactData);
    } catch (error) {
      console.error('Error loading contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const categoryOptions = [
    { value: 'lieferant', label: 'Lieferant' },
    { value: 'kunde', label: 'Kunde' },
    { value: 'dienstleister', label: 'Dienstleister' },
    { value: 'partner', label: 'Partner' },
    { value: 'behörde', label: 'Behörde' },
    { value: 'mieter', label: 'Mieter' },
    { value: 'vermieter', label: 'Vermieter' },
    { value: 'dä_snäckomat', label: 'Dä Snäckomat' },
    { value: 'sonstige', label: 'Sonstige' }
  ];

  // Add contact
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user?.id) return;

    try {
      const contactData = {
        name: formData.name,
        firma: formData.firma,
        telefon: formData.telefon,
        email: formData.email,
        kategorie: formData.kategorie,
        notiz: formData.notiz,
        strasse: formData.strasse,
        plz: formData.plz,
        ort: formData.ort,
        land: formData.land
      };

      const { data, error } = await supabase
        .from('daten')
        .insert({
          user_id: user.id,
          data_type: 'contact',
          data: contactData
        })
        .select()
        .single();

      if (error) throw error;

      setContacts(prev => [{
        id: data.id,
        ...contactData,
        erstelltAm: data.created_at
      }, ...prev]);

      setFormData({
        name: '',
        firma: '',
        telefon: '',
        email: '',
        kategorie: '',
        notiz: '',
        strasse: '',
        plz: '',
        ort: '',
        land: 'Schweiz'
      });
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error adding contact:', error);
    }
  };

  // Edit contact
  const openEditModal = (contact: Kontakt) => {
    setEditingContact(contact);
    setFormData({
      name: contact.name,
      firma: contact.firma || '',
      telefon: contact.telefon || '',
      email: contact.email || '',
      kategorie: contact.kategorie || '',
      notiz: contact.notiz || '',
      strasse: contact.strasse || '',
      plz: contact.plz || '',
      ort: contact.ort || '',
      land: contact.land || 'Schweiz'
    });
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user?.id || !editingContact) return;

    try {
      const contactData = {
        name: formData.name,
        firma: formData.firma,
        telefon: formData.telefon,
        email: formData.email,
        kategorie: formData.kategorie,
        notiz: formData.notiz,
        strasse: formData.strasse,
        plz: formData.plz,
        ort: formData.ort,
        land: formData.land
      };

      const { data, error } = await supabase
        .from('daten')
        .update({
          data: contactData
        })
        .eq('id', editingContact.id)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;

      setContacts(prev => prev.map(contact => 
        contact.id === editingContact.id 
          ? { ...contact, ...contactData }
          : contact
      ));

      setFormData({
        name: '',
        firma: '',
        telefon: '',
        email: '',
        kategorie: '',
        notiz: '',
        strasse: '',
        plz: '',
        ort: '',
        land: 'Schweiz'
      });
      setEditingContact(null);
      setIsEditModalOpen(false);
    } catch (error) {
      console.error('Error updating contact:', error);
    }
  };

  const deleteContact = async (id: string) => {
    if (!user?.id) return;

    if (!confirm('Sind Sie sicher, dass Sie diesen Kontakt löschen möchten?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('daten')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;

      setContacts(prev => prev.filter(contact => contact.id !== id));
    } catch (error) {
      console.error('Error deleting contact:', error);
    }
  };

  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         contact.firma?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         contact.ort?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !categoryFilter || contact.kategorie === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getCategoryLabel = (category: string) => {
    const option = categoryOptions.find(opt => opt.value === category);
    return option?.label || category;
  };

  const getInitials = (name: string, firma: string) => {
    if (name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    if (firma) {
      return firma.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return 'C';
  };

  const resetForm = () => {
    setFormData({
      name: '',
      firma: '',
      telefon: '',
      email: '',
      kategorie: '',
      notiz: '',
      strasse: '',
      plz: '',
      ort: '',
      land: 'Schweiz'
    });
    setEditingContact(null);
  };

  const formatAddress = (contact: Kontakt) => {
    const parts = [];
    if (contact.strasse) parts.push(contact.strasse);
    if (contact.plz && contact.ort) {
      parts.push(`${contact.plz} ${contact.ort}`);
    } else if (contact.ort) {
      parts.push(contact.ort);
    }
    if (contact.land && contact.land !== 'Schweiz') parts.push(contact.land);
    return parts.join(', ');
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-line text-4xl text-blue-600 animate-spin mb-4"></i>
          <p className="text-gray-600">Kontakte werden geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full overflow-x-hidden">
      <div className="w-full max-w-full overflow-x-hidden">
        <div className="bg-white rounded-lg shadow-sm border w-full overflow-x-hidden">
          <div className="px-3 sm:px-4 lg:px-6 py-4 border-b border-gray-200">
            <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:justify-between sm:items-center">
              <div className="min-w-0 flex-1">
                <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 flex items-center">
                  Kontakte
                  <span className="ml-3 text-sm text-blue-600">
                    <i className="ri-cloud-line mr-1"></i>
                    Supabase
                  </span>
                </h1>
                <p className="text-gray-600 mt-1 text-sm sm:text-base break-words">
                  Verwalten Sie Ihre Kontakte ({contacts.length} Kontakte)
                </p>
              </div>
              <button
                onClick={() => {
                  resetForm();
                  setIsModalOpen(true);
                }}
                className="w-full sm:w-auto bg-blue-600 text-white px-4 py-3 sm:py-2 rounded-md hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center flex-shrink-0"
              >
                <i className="ri-add-line w-4 h-4 flex items-center justify-center mr-2"></i>
                Kontakt hinzufügen
              </button>
            </div>
          </div>

          {/* Filter und Suche */}
          {contacts.length > 0 && (
            <div className="px-3 sm:px-4 lg:px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                    <input
                      type="text"
                      placeholder="Kontakte durchsuchen..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <div className="sm:w-48">
                  <Select
                    options={[
                      { value: '', label: 'Alle Kategorien' },
                      ...categoryOptions
                    ]}
                    value={categoryFilter}
                    onChange={(value) => setCategoryFilter(value)}
                  />
                </div>
              </div>
            </div>
          )}

          <div className="p-3 sm:p-4 lg:p-6 w-full overflow-x-hidden">
            {contacts.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-gray-400 mb-4">
                  <i className="ri-contacts-line text-6xl w-16 h-16 flex items-center justify-center mx-auto"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                  Keine Kontakte vorhanden
                </h3>
                <p className="text-gray-500">
                  Fügen Sie Ihren ersten Kontakt hinzu, um zu beginnen.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredContacts.map((contact) => (
                  <div key={contact.id} className="bg-white rounded-lg shadow-sm border p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                          <span className="text-blue-600 font-semibold text-sm">
                            {getInitials(contact.name, contact.firma)}
                          </span>
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{contact.name}</h3>
                          {contact.firma && (
                            <p className="text-sm text-gray-600">{contact.firma}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => openEditModal(contact)}
                          className="text-gray-400 hover:text-blue-600 cursor-pointer"
                          title="Bearbeiten"
                        >
                          <i className="ri-edit-line"></i>
                        </button>
                        <button
                          onClick={() => deleteContact(contact.id)}
                          className="text-gray-400 hover:text-red-600 cursor-pointer"
                          title="Löschen"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <i className="ri-phone-line w-4 h-4 flex items-center justify-center"></i>
                        <span>{contact.telefon || 'Keine Telefonnummer'}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <i className="ri-mail-line w-4 h-4 flex items-center justify-center"></i>
                        <span>{contact.email || 'Keine E-Mail'}</span>
                      </div>
                      {formatAddress(contact) && (
                        <div className="flex items-start space-x-2 text-sm text-gray-600">
                          <i className="ri-map-pin-line w-4 h-4 flex items-center justify-center mt-0.5"></i>
                          <span className="flex-1">{formatAddress(contact)}</span>
                        </div>
                      )}
                      {contact.notiz && (
                        <div className="flex items-start space-x-2 text-sm text-gray-600">
                          <i className="ri-sticky-note-line w-4 h-4 flex items-center justify-center mt-0.5"></i>
                          <span className="flex-1">{contact.notiz}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between mt-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {getCategoryLabel(contact.kategorie)}
                        </span>
                        <span className="text-xs text-gray-500">
                          {new Date(contact.erstelltAm).toLocaleDateString('de-DE')}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Contact Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          resetForm();
        }}
        title="Neuen Kontakt hinzufügen"
        maxWidth="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Name"
              placeholder="Vollständiger Name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              required
            />
            <Input
              label="Firma"
              placeholder="Firmenname"
              value={formData.firma}
              onChange={(e) => handleInputChange('firma', e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Telefonnummer"
              type="tel"
              placeholder="+41 123 456789"
              value={formData.telefon}
              onChange={(e) => handleInputChange('telefon', e.target.value)}
            />
            <Input
              label="E-Mail-Adresse"
              type="email"
              placeholder="kontakt@beispiel.ch"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
            />
          </div>

          {/* Adresse Sektion */}
          <div className="border-t pt-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <i className="ri-map-pin-line w-5 h-5 flex items-center justify-center mr-2 text-gray-600"></i>
              Adresse
            </h3>
            
            <div className="space-y-4">
              <Input
                label="Straße und Hausnummer"
                placeholder="Musterstraße 123"
                value={formData.strasse}
                onChange={(e) => handleInputChange('strasse', e.target.value)}
              />
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  label="PLZ"
                  placeholder="8000"
                  value={formData.plz}
                  onChange={(e) => handleInputChange('plz', e.target.value)}
                />
                <Input
                  label="Ort"
                  placeholder="Zürich"
                  value={formData.ort}
                  onChange={(e) => handleInputChange('ort', e.target.value)}
                />
                <Input
                  label="Land"
                  placeholder="Schweiz"
                  value={formData.land}
                  onChange={(e) => handleInputChange('land', e.target.value)}
                />
              </div>
            </div>
          </div>

          <Select
            label="Kategorie"
            options={categoryOptions}
            value={formData.kategorie}
            onChange={(value) => setFormData(prev => ({ ...prev, kategorie: value as string }))}
            placeholder="Kategorie auswählen"
            required
          />

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notiz
            </label>
            <textarea
              value={formData.notiz}
              onChange={(e) => setFormData(prev => ({ ...prev, notiz: e.target.value }))}
              placeholder="Zusätzliche Informationen über den Kontakt..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>

          <div className="flex space-x-3 pt-4">
            <Button type="submit" className="flex-1">
              Kontakt hinzufügen
            </Button>
            <Button 
              type="button" 
              variant="secondary" 
              onClick={() => {
                setIsModalOpen(false);
                resetForm();
              }}
            >
              Abbrechen
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit Contact Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          resetForm();
        }}
        title="Kontakt bearbeiten"
        maxWidth="lg"
      >
        <form onSubmit={handleEditSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Name"
              placeholder="Vollständiger Name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              required
            />
            <Input
              label="Firma"
              placeholder="Firmenname"
              value={formData.firma}
              onChange={(e) => handleInputChange('firma', e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Telefonnummer"
              type="tel"
              placeholder="+41 123 456789"
              value={formData.telefon}
              onChange={(e) => handleInputChange('telefon', e.target.value)}
            />
            <Input
              label="E-Mail-Adresse"
              type="email"
              placeholder="kontakt@beispiel.ch"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
            />
          </div>

          {/* Adresse Sektion */}
          <div className="border-t pt-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <i className="ri-map-pin-line w-5 h-5 flex items-center justify-center mr-2 text-gray-600"></i>
              Adresse
            </h3>
            
            <div className="space-y-4">
              <Input
                label="Straße und Hausnummer"
                placeholder="Musterstraße 123"
                value={formData.strasse}
                onChange={(e) => handleInputChange('strasse', e.target.value)}
              />
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  label="PLZ"
                  placeholder="8000"
                  value={formData.plz}
                  onChange={(e) => handleInputChange('plz', e.target.value)}
                />
                <Input
                  label="Ort"
                  placeholder="Zürich"
                  value={formData.ort}
                  onChange={(e) => handleInputChange('ort', e.target.value)}
                />
                <Input
                  label="Land"
                  placeholder="Schweiz"
                  value={formData.land}
                  onChange={(e) => handleInputChange('land', e.target.value)}
                />
              </div>
            </div>
          </div>

          <Select
            label="Kategorie"
            options={categoryOptions}
            value={formData.kategorie}
            onChange={(value) => setFormData(prev => ({ ...prev, kategorie: value as string }))}
            placeholder="Kategorie auswählen"
            required
          />

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notiz
            </label>
            <textarea
              value={formData.notiz}
              onChange={(e) => setFormData(prev => ({ ...prev, notiz: e.target.value }))}
              placeholder="Zusätzliche Informationen über den Kontakt..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>

          <div className="flex space-x-3 pt-4">
            <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700">
              Änderungen speichern
            </Button>
            <Button 
              type="button" 
              variant="secondary" 
              onClick={() => {
                setIsEditModalOpen(false);
                resetForm();
              }}
            >
              Abbrechen
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
